import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Table — dense, clean, Linear-style                                */
/* ------------------------------------------------------------------ */

const css = /* css */ `
  .sf-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .sf-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--sf-font-sans);
    font-size: var(--sf-text-sm);
    text-align: left;
  }

  .sf-table thead {
    border-bottom: 1px solid var(--sf-border);
  }

  .sf-table th {
    padding: var(--sf-space-2) var(--sf-space-3);
    font-weight: var(--sf-weight-medium);
    color: var(--sf-text-muted);
    font-size: var(--sf-text-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    background: var(--sf-bg-inset);
  }

  .sf-table td {
    padding: var(--sf-space-2) var(--sf-space-3);
    color: var(--sf-text);
    border-bottom: 1px solid var(--sf-gray-100);
    vertical-align: middle;
  }

  .sf-table tbody tr {
    transition: background var(--sf-transition-fast);
  }

  .sf-table tbody tr:hover {
    background: var(--sf-gray-50);
  }

  .sf-table tbody tr:last-child td {
    border-bottom: none;
  }
`;

/* -- Table --------------------------------------------------------- */

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Wrap in a scrollable container. Default true. */
  scrollable?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ scrollable = true, children, className, ...rest }, ref) => {
    injectStyles("sf-table", css);
    const classes = ["sf-table", className ?? ""].filter(Boolean).join(" ");
    const table = (
      <table ref={ref} className={classes} {...rest}>
        {children}
      </table>
    );
    if (scrollable) return <div className="sf-table-wrap">{table}</div>;
    return table;
  },
);
Table.displayName = "Table";

/* -- TableHead ----------------------------------------------------- */

export type TableHeadProps = React.HTMLAttributes<HTMLTableSectionElement>;

const TableHead = React.forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ children, ...rest }, ref) => (
    <thead ref={ref} {...rest}>
      {children}
    </thead>
  ),
);
TableHead.displayName = "TableHead";

/* -- TableBody ----------------------------------------------------- */

export type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement>;

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ children, ...rest }, ref) => (
    <tbody ref={ref} {...rest}>
      {children}
    </tbody>
  ),
);
TableBody.displayName = "TableBody";

/* -- TableRow ------------------------------------------------------ */

export type TableRowProps = React.HTMLAttributes<HTMLTableRowElement>;

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, ...rest }, ref) => (
    <tr ref={ref} {...rest}>
      {children}
    </tr>
  ),
);
TableRow.displayName = "TableRow";

/* -- TableHeaderCell ----------------------------------------------- */

export type TableHeaderCellProps =
  React.ThHTMLAttributes<HTMLTableCellElement>;

const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  TableHeaderCellProps
>(({ children, ...rest }, ref) => (
  <th ref={ref} {...rest}>
    {children}
  </th>
));
TableHeaderCell.displayName = "TableHeaderCell";

/* -- TableCell ----------------------------------------------------- */

export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement>;

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ children, ...rest }, ref) => (
    <td ref={ref} {...rest}>
      {children}
    </td>
  ),
);
TableCell.displayName = "TableCell";

export { Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell };
